/**
 * 运营 - OpenApi - 对接客户详情（公钥与 IP 白名单配置）
 */
<template>
  <div class="openapi-client-detail-page">
    <el-breadcrumb separator-class="el-icon-arrow-right" class="page-breadcrumb">
      <el-breadcrumb-item :to="{ path: '/oms/customerSignOrder' }">运营</el-breadcrumb-item>
      <el-breadcrumb-item :to="{ path: '/oms/openapiClientList' }">OpenApi</el-breadcrumb-item>
      <el-breadcrumb-item :to="{ path: '/oms/openapiClientList' }">对接客户</el-breadcrumb-item>
      <el-breadcrumb-item>客户详情</el-breadcrumb-item>
    </el-breadcrumb>

    <el-card shadow="hover" class="detail-card" v-loading="loading" element-loading-text="加载中...">
      <div class="section-title">基本信息</div>
      <el-descriptions :column="2" size="small" border class="info-descriptions">
        <el-descriptions-item label="客户名称">{{ clientInfo.clientName || '--' }}</el-descriptions-item>
        <el-descriptions-item label="应用 ID">{{ clientInfo.appId || '--' }}</el-descriptions-item>
        <el-descriptions-item label="商户名称">{{ clientInfo.companyName || '--' }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ clientInfo.createdAt || '--' }}</el-descriptions-item>
      </el-descriptions>

      <div class="section-title">安全配置</div>
      <el-form
        ref="configFormRef"
        :model="form"
        :rules="rules"
        label-width="120px"
        size="small"
        class="config-form"
      >
        <el-form-item label="客户 RSA 公钥" prop="rsaPublicKey">
          <el-input
            v-model="form.rsaPublicKey"
            type="textarea"
            :rows="3"
            placeholder="请输入 Base64 单行公钥（不含 PEM 头尾，如 MIIBIjANBgkqhkiG9w0BAQEF...）"
          />
        </el-form-item>
        <el-form-item label="IP 白名单" prop="ipWhitelist">
          <el-input
            v-model="form.ipWhitelist"
            type="textarea"
            :rows="4"
            placeholder="多个 IP 或网段请用英文逗号分隔，如：1.2.3.4,10.0.0.0/24"
          />
        </el-form-item>
      </el-form>

      <div class="action-bar">
        <el-button size="small" icon="el-icon-back" @click="goBack">返回</el-button>
        <el-button size="small" type="primary" :loading="submitting" @click="submitConfig">
          保存配置
        </el-button>
      </div>
    </el-card>
  </div>
</template>

<script>
import { getOpenapiClientDetail, updateOpenapiClientConfig } from '../../api/userMG';

function isValidIpv4(ip) {
  const parts = ip.split('.');
  if (parts.length !== 4) return false;
  return parts.every((part) => {
    if (!/^\d{1,3}$/.test(part)) return false;
    const num = Number(part);
    return num >= 0 && num <= 255;
  });
}

function isValidIpOrCidr(value) {
  const str = String(value || '').trim();
  if (!str) return false;
  if (str.includes('/')) {
    const [ip, prefix] = str.split('/');
    if (!isValidIpv4(ip)) return false;
    if (!/^\d{1,2}$/.test(prefix)) return false;
    const prefixNum = Number(prefix);
    return prefixNum >= 0 && prefixNum <= 32;
  }
  return isValidIpv4(str);
}

function normalizeBase64PublicKey(value) {
  return String(value || '').replace(/\s+/g, '');
}

function createDefaultForm() {
  return {
    clientId: '',
    rsaPublicKey: '',
    ipWhitelist: ''
  };
}

export default {
  name: 'OpenapiClientDetail',
  data() {
    return {
      loading: false,
      submitting: false,
      clientInfo: {
        clientName: '',
        appId: '',
        companyName: '',
        createdAt: ''
      },
      form: createDefaultForm(),
      rules: {
        rsaPublicKey: [
          { required: true, message: '请输入客户 RSA 公钥', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              const key = normalizeBase64PublicKey(value);
              if (!key) {
                callback(new Error('请输入客户 RSA 公钥'));
                return;
              }
              if (/-----BEGIN|-----END/.test(String(value || ''))) {
                callback(new Error('请勿包含 PEM 头尾，仅填写 Base64 单行内容'));
                return;
              }
              if (!/^[A-Za-z0-9+/=]+$/.test(key)) {
                callback(new Error('公钥格式不正确，请填写 Base64 字符串'));
                return;
              }
              callback();
            },
            trigger: 'blur'
          }
        ],
        ipWhitelist: [
          { required: true, message: '请输入 IP 白名单', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              const items = String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
              if (!items.length) {
                callback(new Error('请输入 IP 白名单'));
                return;
              }
              const invalid = items.find((item) => !isValidIpOrCidr(item));
              if (invalid) {
                callback(new Error(`IP 或网段格式不正确：${invalid}`));
                return;
              }
              callback();
            },
            trigger: 'blur'
          }
        ]
      }
    };
  },
  created() {
    this.initPage();
  },
  methods: {
    initPage() {
      const query = this.$route.query || {};
      const clientId = query.clientId || query.id;
      if (!clientId) {
        this.$message.warning('缺少客户 ID，请从列表页进入');
        this.goBack();
        return;
      }
      this.form.clientId = clientId;
      this.clientInfo = {
        clientName: query.clientName || '',
        appId: query.appId || '',
        companyName: query.companyName || '',
        createdAt: query.createdAt || ''
      };
      this.loadDetail();
    },
    loadDetail() {
      this.loading = true;
      getOpenapiClientDetail({ clientId: this.form.clientId })
        .then((res) => {
          if (res && (res.msg === 'success' || res.code === 0)) {
            const data = res.data || {};
            this.form.clientId = data.clientId != null ? data.clientId : this.form.clientId;
            this.form.rsaPublicKey = data.rsaPublicKey || '';
            this.form.ipWhitelist = data.ipWhitelist || '';
          } else {
            this.$message.error((res && (res.msg || res.message)) || '获取客户详情失败');
          }
        })
        .catch((err) => {
          const data = err && err.response && err.response.data;
          this.$message.error((data && (data.msg || data.message)) || (err && err.message) || '获取客户详情失败');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    submitConfig() {
      this.$refs.configFormRef.validate((valid) => {
        if (!valid) return;
        const clientId = Number(this.form.clientId);
        if (!clientId) {
          this.$message.warning('客户 ID 无效');
          return;
        }
        this.submitting = true;
        updateOpenapiClientConfig({
          clientId,
          rsaPublicKey: normalizeBase64PublicKey(this.form.rsaPublicKey),
          ipWhitelist: String(this.form.ipWhitelist || '')
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
            .join(',')
        })
          .then((res) => {
            if (res && (res.msg === 'success' || res.code === 0)) {
              this.$message.success('保存成功');
              this.loadDetail();
            } else {
              this.$message.error((res && (res.msg || res.message)) || '保存失败');
            }
          })
          .catch((err) => {
            const data = err && err.response && err.response.data;
            this.$message.error((data && (data.msg || data.message)) || (err && err.message) || '保存失败');
          })
          .finally(() => {
            this.submitting = false;
          });
      });
    },
    goBack() {
      this.$router.push({ path: '/oms/openapiClientList' });
    }
  }
};
</script>

<style scoped>
.openapi-client-detail-page {
  padding-bottom: 24px;
}
.page-breadcrumb {
  margin-bottom: 0;
}
.detail-card {
  margin-top: 24px;
  border-radius: 8px;
}
.detail-card >>> .el-card__body {
  padding: 20px 24px 24px;
}
.section-title {
  margin: 0 0 16px;
  padding-left: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  border-left: 3px solid #409eff;
}
.section-title:not(:first-of-type) {
  margin-top: 24px;
}
.info-descriptions {
  margin-bottom: 8px;
}
.config-form {
  max-width: 900px;
}
.action-bar {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
  text-align: right;
}
.action-bar .el-button + .el-button {
  margin-left: 10px;
}
</style>



// WEBPACK FOOTER //
// src/views/oms/openapiClientDetail.vue